import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../config/apiConfig';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Tooltip } from '../components/Tooltip';

// Add custom scrollbar styles to the head
const addCustomScrollbarStyles = () => {
  const style = document.createElement('style');
  style.textContent = `
    .custom-scrollbar::-webkit-scrollbar {
      width: 8px;
    }
    .custom-scrollbar::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 4px;
    }
    .custom-scrollbar.dark::-webkit-scrollbar-track {
      background: #2d3748;
      border-radius: 4px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: #c5c5c5;
      border-radius: 4px;
    }
    .custom-scrollbar.dark::-webkit-scrollbar-thumb {
      background: #4a5568;
      border-radius: 4px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: #a8a8a8;
    }
    .custom-scrollbar.dark::-webkit-scrollbar-thumb:hover {
      background: #718096;
    }
  `;
  document.head.appendChild(style);
};

interface ApiResponse {
  suggestions: string[] | string;
  error?: string;
}

interface CommitSuggestion {
  category: string;
  messages: string[];
}

export const GitCommitGeniePage = () => {
  // Add custom scrollbar styles when component mounts
  useEffect(() => {
    addCustomScrollbarStyles();
  }, []);
  
  const [gitDiff, setGitDiff] = useState('');
  const [suggestions, setSuggestions] = useState<CommitSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedSuggestion, setSelectedSuggestion] = useState<CommitSuggestion | null>(null);
  const [copiedMessage, setCopiedMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  
  const handleClear = () => {
    setGitDiff('');
    setSuggestions([]);
    setSelectedSuggestion(null);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!gitDiff.trim()) {
      setError('Please enter git diff content before submitting.');
      return;
    }
    
    setIsLoading(true);
    setError('');
    setSuggestions([]);
    setSelectedSuggestion(null);
    
    try {
      const apiUrl = getApiUrl('GIT_COMMIT_SUGGESTIONS');
      console.log('Calling API at:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(gitDiff),
      });
      
      console.log('API Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      
      // Get the raw text first to debug
      const rawText = await response.text();
      console.log('Raw API response text:', rawText);
      
      // Parse the JSON manually
      let data;
      try {
        data = JSON.parse(rawText);
        console.log('Parsed API Response:', data);
      } catch (parseError) {
        console.error('Error parsing JSON:', parseError);
        setSuggestions([{
          category: 'API Response (Raw)',
          messages: [rawText]
        }]);
        return;
      }
      
      // Handle the case where the API returns a suggestions array directly
      if (data && data.suggestions) {
        console.log('Found suggestions property in response');
        
        // Handle string suggestions
        if (typeof data.suggestions === 'string') {
          setSuggestions([{
            category: 'Commit Suggestions',
            messages: [data.suggestions]
          }]);
          return;
        }
        
        // Handle array of suggestions
        if (Array.isArray(data.suggestions)) {
          // First try to parse into categories
          const processedSuggestions: CommitSuggestion[] = [];
          let currentCategory = '';
          let currentMessages: string[] = [];
          let hasCategories = false;
          
          // Check if any item looks like a category header
          const hasCategoryHeaders = data.suggestions.some(
            (item: string) => typeof item === 'string' && item.startsWith('**') && item.includes(':**')
          );
          
          if (hasCategoryHeaders) {
            data.suggestions.forEach((suggestion: string, index: number) => {
              if (typeof suggestion !== 'string') return;
              
              // Check if this is a category header
              if (suggestion.startsWith('**') && suggestion.includes(':**')) {
                // Save previous category if it exists
                if (currentCategory && currentMessages.length > 0) {
                  processedSuggestions.push({
                    category: currentCategory,
                    messages: [...currentMessages]
                  });
                  hasCategories = true;
                }
                
                // Start a new category
                currentCategory = suggestion.replace(/^\*\*|:\*\*$/g, '');
                currentMessages = [];
              } 
              // Check if this is a bullet point message
              else if (suggestion.startsWith('*')) {
                currentMessages.push(suggestion.replace(/^\* /, ''));
              }
              // Skip the first item which is usually an introduction
              else if (index > 0) {
                // If it's not a category or bullet point but not the first item, treat as additional info
                currentMessages.push(suggestion);
              }
            });
            
            // Add the last category if it exists
            if (currentCategory && currentMessages.length > 0) {
              processedSuggestions.push({
                category: currentCategory,
                messages: [...currentMessages]
              });
              hasCategories = true;
            }
            
            // If we successfully parsed categories, use them
            if (hasCategories) {
              console.log('Successfully parsed categories:', processedSuggestions);
              setSuggestions(processedSuggestions);
              return;
            }
          }
          
          // If we couldn't parse categories or there weren't any, just display all suggestions
          console.log('Displaying all suggestions without categories');
          const validSuggestions = data.suggestions.filter(
            (item: any) => item && typeof item === 'string' && item.trim().length > 0
          );
          
          if (validSuggestions.length > 0) {
            setSuggestions([{
              category: 'Commit Suggestions',
              messages: validSuggestions
            }]);
          } else {
            setError('No valid commit suggestions found in the response');
          }
          return;
        }
      }
      
      // If we get here, we couldn't process the response in any expected format
      // Try to display it as is
      console.log('Unexpected data format, displaying raw data');
      let displayText: string;
      
      try {
        displayText = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
      } catch (err) {
        displayText = rawText || 'Unable to display response data';
      }
      
      setSuggestions([{
        category: 'API Response',
        messages: [displayText]
      }]);
    } catch (error) {
      console.error('Error calling API:', error);
      setError(`Failed to get commit suggestions: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopySuggestion = (message: string) => {
    navigator.clipboard.writeText(message)
      .then(() => {
        setSelectedSuggestion({ category: '', messages: [message] });
        setCopiedMessage(message);
        setTimeout(() => {
          setSelectedSuggestion(null);
          setCopiedMessage(null);
        }, 2000);
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
      });
  };

  const loadSampleDiff = () => {
    const sampleDiff = `diff --git a/src/components/UserProfile.tsx b/src/components/UserProfile.tsx
index 8d7e621..9f31c02 100644
--- a/src/components/UserProfile.tsx
+++ b/src/components/UserProfile.tsx
@@ -15,7 +15,7 @@ export const UserProfile = ({ userId }: UserProfileProps) => {
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState('');
 
-  // Fetch user data when component mounts
+  // Fetch user data when component mounts or userId changes
   useEffect(() => {
     const fetchUserData = async () => {
       try {
@@ -23,6 +23,7 @@ export const UserProfile = ({ userId }: UserProfileProps) => {
         const data = await response.json();
         setUserData(data);
         setLoading(false);
+        console.log('User data loaded:', data);
       } catch (err) {
         setError('Failed to load user profile');
         setLoading(false);
@@ -30,7 +31,7 @@ export const UserProfile = ({ userId }: UserProfileProps) => {
     };
 
     fetchUserData();
-  }, []);
+  }, [userId]);
 
   if (loading) return <div>Loading...</div>;
   if (error) return <div>{error}</div>;`;
    
    setGitDiff(sampleDiff);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-6 shadow-md">
        <div className="container mx-auto px-4">
          <button 
            onClick={() => navigate('/')}
            className="mb-3 px-3 py-1 bg-white/20 rounded hover:bg-white/40 text-sm transition-all duration-300 active:scale-90 active:translate-x-[-4px]"
          >
            ← Back
          </button>
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-2">Git Commit Genie</h1>
            <p className="text-blue-100">Paste a `git diff` and get AI-suggested conventional commit messages</p>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Git Diff Input Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden mb-6">
          <div className="p-6">
            <h2 className="font-bold mb-4 text-lg flex items-center dark:text-white">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Git Diff:
            </h2>
              
            <form onSubmit={handleSubmit}>
              <div className="mb-4 relative">
                <div className="flex justify-between items-center mb-2">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {gitDiff ? `${gitDiff.length} characters` : 'Paste your git diff below'}
                  </div>
                  <div className="flex">
                    {!gitDiff && (
                      <Tooltip content="Load a sample git diff">
                        <button 
                          type="button"
                          onClick={loadSampleDiff}
                          className="text-gray-400 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 px-3 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 mr-1 text-sm flex items-center gap-1"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Sample diff
                        </button>
                      </Tooltip>
                    )}
                    {gitDiff && (
                      <button 
                        type="button"
                        onClick={handleClear}
                        className="text-gray-400 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 px-3 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/30 text-sm flex items-center gap-1"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Clear input
                      </button>
                    )}
                  </div>
                </div>
                <div className="relative">
                  <textarea
                    id="gitDiff"
                    value={gitDiff}
                    onChange={(e) => setGitDiff(e.target.value)}
                    className="w-full p-4 border border-gray-300 dark:border-gray-700 rounded-lg font-mono text-sm h-[400px] bg-gray-50 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="Paste your git diff here..."
                    required
                  />
                </div>
              </div>
              
              <button 
                type="submit" 
                disabled={isLoading || !gitDiff.trim()}
                className={`w-full py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center ${isLoading || !gitDiff.trim() 
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                  : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg'}`}
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner size="small" />
                    <span className="ml-2">Generating suggestions...</span>
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Generate Commit Messages
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
            
        {/* Suggested Commit Messages Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-bold text-lg flex items-center dark:text-white">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Suggested Commit Messages:
              </h2>
            </div>
              
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 h-[400px] overflow-hidden">
              {isLoading ? (
                <div className="h-full flex flex-col items-center justify-center p-6">
                  <LoadingSpinner size="large" />
                  <p className="mt-4 text-gray-600">Analyzing git diff and generating commit suggestions...</p>
                </div>
              ) : error ? (
                <div className="p-6 h-full flex items-center justify-center">
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 max-w-md text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto mb-3 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p>{error}</p>
                  </div>
                </div>
              ) : suggestions && suggestions.length > 0 ? (
                <div className="h-full overflow-y-auto custom-scrollbar p-1">
                  {suggestions.map((categoryGroup, groupIndex) => (
                    <div key={groupIndex} className="mb-4 rounded-lg overflow-hidden shadow-sm border border-gray-200 dark:border-gray-700">
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-3 font-medium text-gray-800 dark:text-gray-200 border-b border-gray-200 dark:border-gray-700">
                        {categoryGroup.category}
                      </div>
                      <div className="divide-y divide-gray-100 dark:divide-gray-700">
                        {categoryGroup.messages.map((message, msgIndex) => {
                          const isSelected = selectedSuggestion?.messages[0] === message;
                          return (
                            <div 
                              key={`${groupIndex}-${msgIndex}`} 
                              className={`p-4 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors cursor-pointer ${isSelected ? 'bg-green-50 dark:bg-green-900/30' : 'bg-white dark:bg-gray-800'}`}
                              onClick={() => handleCopySuggestion(message)}
                            >
                              <div className="flex justify-between items-start gap-3">
                                <p className="text-sm whitespace-pre-wrap break-words leading-relaxed dark:text-gray-300">{message}</p>
                                <Tooltip content={isSelected ? "Copied!" : "Copy to clipboard"}>
                                  <button 
                                    className={`ml-2 flex-shrink-0 p-1.5 rounded-full ${isSelected 
                                      ? 'bg-green-100 text-green-600' 
                                      : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCopySuggestion(message);
                                    }}
                                  >
                                    {isSelected ? (
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                    ) : (
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                      </svg>
                                    )}
                                  </button>
                                </Tooltip>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center p-6 text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mb-4 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-gray-500 dark:text-gray-400">No suggestions yet</p>
                  <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">Paste your git diff and click "Generate Commit Messages"</p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Usage Tips */}
        <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <h3 className="font-bold text-lg mb-3 text-gray-800 dark:text-white">Tips for Better Results</h3>
          <ul className="list-disc pl-5 space-y-2 text-gray-600 dark:text-gray-300">
            <li>Include the complete diff output from git for more accurate suggestions</li>
            <li>For larger changes, consider splitting your commit into smaller, focused changes</li>
            <li>Click on any suggestion to copy it to your clipboard</li>
            <li>You can use these suggestions directly in your git commit command</li>
            <li>Try using <code className="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded text-sm font-mono dark:text-gray-300">git diff --staged</code> to get changes you've already added to staging</li>
          </ul>
        </div>
        
        {/* Command Reference */}
        <div className="mt-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <h3 className="font-bold text-lg mb-3 text-gray-800 dark:text-white">Git Command Reference</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h4 className="font-medium text-blue-700 dark:text-blue-400 mb-2">Get diff for staged changes</h4>
              <div className="bg-gray-800 text-white p-3 rounded font-mono text-sm overflow-x-auto">
                git diff --staged
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h4 className="font-medium text-blue-700 dark:text-blue-400 mb-2">Get diff for all changes</h4>
              <div className="bg-gray-800 text-white p-3 rounded font-mono text-sm overflow-x-auto">
                git diff
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h4 className="font-medium text-blue-700 dark:text-blue-400 mb-2">Commit with suggested message</h4>
              <div className="bg-gray-800 text-white p-3 rounded font-mono text-sm overflow-x-auto whitespace-pre-wrap">
                git commit -m "feat: improve user profile data loading"
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h4 className="font-medium text-blue-700 dark:text-blue-400 mb-2">Get diff for specific file</h4>
              <div className="bg-gray-800 text-white p-3 rounded font-mono text-sm overflow-x-auto">
                git diff path/to/file.js
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GitCommitGeniePage;